import { request } from "./http";

const LOCAL_ONBOARDING_STATE_KEY = "bs:onboarding:fallback-state";

function canFallback(error) {
  const status = Number(error?.status || error?.response?.status || 0);
  return status === 404 || status === 405 || status === 501;
}

function readLocalState() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_ONBOARDING_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeLocalState(patch = {}) {
  const next = {
    ...readLocalState(),
    ...patch,
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(LOCAL_ONBOARDING_STATE_KEY, JSON.stringify(next));
    } catch {
      // noop
    }
  }
  return next;
}

function buildFallbackResponse(patch) {
  const state = patch ? writeLocalState(patch) : readLocalState();
  const dismissed = Boolean(state.dismissed);
  const completed = Boolean(state.completed);
  return {
    dismissed,
    completed,
    shouldShow: !dismissed && !completed,
    state: {
      dismissed,
      completed,
      tourCompleted: Boolean(state.tourCompleted),
      lastSeenStepId: String(state.lastSeenStepId || "").trim(),
    },
  };
}

export const onboardingApi = {
  async get() {
    try {
      return await request("/api/me/onboarding");
    } catch (error) {
      if (!canFallback(error)) throw error;
      return buildFallbackResponse();
    }
  },
  async update(payload = {}) {
    try {
      return await request("/api/me/onboarding", { method: "PUT", json: payload });
    } catch (error) {
      if (!canFallback(error)) throw error;
      return buildFallbackResponse(payload);
    }
  },
  async dismiss() {
    try {
      return await request("/api/me/onboarding", { method: "PUT", json: { dismissed: true } });
    } catch (error) {
      if (!canFallback(error)) throw error;
      return buildFallbackResponse({ dismissed: true });
    }
  },
  async complete() {
    try {
      return await request("/api/me/onboarding/complete", { method: "POST", json: {} });
    } catch (error) {
      if (!canFallback(error)) throw error;
      return this.update({ completed: true, tourCompleted: true });
    }
  },
  async restart() {
    try {
      return await request("/api/me/onboarding/restart", { method: "POST", json: {} });
    } catch (error) {
      if (!canFallback(error)) throw error;
      return buildFallbackResponse({
        dismissed: false,
        completed: false,
        tourCompleted: false,
        lastSeenStepId: "",
      });
    }
  },
  async tourComplete(lastSeenStepId) {
    try {
      return await request("/api/me/onboarding/tour-complete", { method: "POST", json: { lastSeenStepId } });
    } catch (error) {
      if (!canFallback(error)) throw error;
      return this.update({ tourCompleted: true, lastSeenStepId });
    }
  },
};
